import React, { Fragment, useEffect, useState } from 'react'
import { Button, Chip, Divider, Typography } from '@themenu/design-system'
import Link from 'next/link'
import { eventSenderManager } from 'lib/analytics/eventSender'
import { NotificationItem as TNotificationItem } from 'providers/notifications/types'
import { useReadNotifications } from 'providers/notifications/useReadNotifications'
import NotificationItem from 'pages/notifications/components/NotificationItem'
import { cn } from 'utils'

interface Props {
  onClose: () => void
  isOpen: boolean
  list: TNotificationItem[]
}

const NotificationList: React.FC<Props> = ({ isOpen, list, onClose }) => {
  const { trigger: readNotifications } = useReadNotifications()
  const [hasReaded, setHasReaded] = useState(false)

  const notReadQuantity = list.filter(({ read }) => !read).length
  const quantityLabel = notReadQuantity === 1 ? 'Nova' : 'Novas'

  useEffect(() => {
    if (isOpen && !hasReaded) {
      const idsToRead = list
        .filter(({ read }) => !read)
        .map(({ notificationId }) => notificationId)

      if (idsToRead.length > 0) {
        readNotifications({ ids: idsToRead })

        const quantityByFeature = list.reduce((acc, { feature }) => {
          acc[feature] = (acc[feature] || 0) + 1
          return acc
        }, {} as Record<string, number>)

        eventSenderManager.sendEvent({
          label: 'notification list marked as read',
          metadata: {
            count: idsToRead.length,
            totalCount: list.length,
            ids: idsToRead,
            ...quantityByFeature
          }
        })
      }

      setHasReaded(true)
    }
  }, [isOpen, list, hasReaded, readNotifications])

  return (
    <div
      className={cn(
        'absolute top-[120%] right-0 bg-white-100 rounded-8 shadow-low z-10 overflow-y-auto transition-all duration-300 transform max-h-[468px]',
        'w-[90vw] md:w-[523px]',
        'left-1/2 md:left-[unset]',
        '-translate-x-1/2 md:translate-x-0',
        'ml-[165%] md:ml-0',
        isOpen
          ? 'opacity-100 scale-100 pointer-events-auto'
          : 'opacity-0 scale-95 pointer-events-none'
      )}
    >
      <div className="flex flex-col md:flex-row gap-8 p-16 items-center justify-between">
        <Typography variant="headline">Notificações</Typography>
        <div className="grid grid-cols-2 gap-8">
          <Chip
            labelVariant="footnote"
            colorSet="primary"
            label={`${notReadQuantity} ${quantityLabel}`}
          />
          <Link
            href="/notifications"
            onClick={() => {
              eventSenderManager.sendEvent({
                label: 'notification list view all clicked',
                metadata: {
                  notReadQuantity,
                  totalCount: list.length
                }
              })
              onClose()
            }}
          >
            <Button
              variant="alternativeText"
              className="text-content-primary"
              size={32}
            >
              Ver todas
            </Button>
          </Link>
        </div>
      </div>
      <Divider />
      <div className="grid gap-16 p-16">
        {list.map((item, i) => (
          <Fragment key={item.notificationId}>
            <NotificationItem item={item} onLinkClick={onClose} />
            {i !== list.length - 1 && <Divider />}
          </Fragment>
        ))}
        {list.length === 0 && (
          <Typography variant="callout" className="text-content-subdued">
            Nenhuma notificação para ser lida
          </Typography>
        )}
      </div>
    </div>
  )
}

export default NotificationList
